import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';
import type { dynamicObj } from 'src/pages/assets';
import { reactive } from 'vue';

export default class MasterDataApiService extends AbstractApiService {
  public uploadState = reactive({
    uploadProgress: '',
    totalRecords: 0,
    uploadedRecords: 0,
  });
  public constructor() {
    super({});
  }

  public async triggerCustomRecord(record_id: string): Promise<any> {
    return super.get(api.trigger_custom_record + '/' + record_id, {});
  }

  public async deleteCustomRecord(record_id: string): Promise<any> {
    const formData = new FormData();
    formData.append('id', record_id);
    return super.delete(api.delete_custom_record, formData);
  }

  createFormData(
    endpoint_information: any,
    record_type = '',
    mode = '',
  ): FormData {
    const formData = new FormData();
    if (mode == 'create') {
      formData.append('record_type', record_type);
    } else if (mode == 'edit') {
      formData.append('id', endpoint_information.id);
    }
    if (endpoint_information.sub_org_id) {
      formData.append('sub_org_id', endpoint_information.sub_org_id);
    }
    if (endpoint_information.auth_type.value === 'ionapi') {
      formData.append(
        'url',
        endpoint_information.dynamic_api
          ? endpoint_information.url_user_input_formatted
          : endpoint_information.url_user_input,
      );
    } else {
      formData.append('url', endpoint_information.url);
    }
    formData.append('method', endpoint_information.method.value);
    formData.append('body_type', endpoint_information.body_type.value);
    formData.append(
      'authentication_type',
      endpoint_information.auth_type.value,
    );
    formData.append('is_dynamic', endpoint_information.dynamic_api);
    const columns: any = {};
    endpoint_information.columns.forEach((column: any) => {
      columns[column.key] = column.value;
    });
    const params: any = {};
    if (endpoint_information.params) {
      endpoint_information.params.forEach((param: any) => {
        params[param.key] = param.value;
      });
    }
    let bodys: any = {};
    if (endpoint_information.body_type.value == 'multipart/form-data') {
      if (endpoint_information.body) {
        endpoint_information.body.forEach((body: any) => {
          bodys[body.key] = body.value;
        });
      }
    } else {
      if (endpoint_information.body_string) {
        bodys = JSON.parse(endpoint_information.body_string);
      }
    }
    const headers: any = {};
    if (endpoint_information.headers) {
      endpoint_information.headers.forEach((header: any) => {
        headers[header.key] = header.value;
      });
    }

    if (Object.keys(columns).length !== 0) {
      formData.append('column_paths', JSON.stringify(columns));
    }
    if (Object.keys(params).length !== 0) {
      formData.append('params', JSON.stringify(params));
    }
    if (Object.keys(bodys).length !== 0) {
      formData.append('body', JSON.stringify(bodys));
    }
    if (Object.keys(headers).length !== 0) {
      formData.append('column_paths', JSON.stringify(headers));
    }
    if (endpoint_information.auth_type != 'none') {
      formData.append('authentication_json', endpoint_information.auth_json);
    }
    return formData;
  }

  public async getMasterDataList(record_type: string): Promise<any> {
    const querystring = this.encodeQueryData({ record_type: record_type });
    return super.get(api.get_master_data_list + '?' + querystring, {});
  }
  public async testEndpointResponse(endpoint_information: any): Promise<any> {
    const formData = this.createFormData(endpoint_information);
    return super.post(api.test_endpoint_response, formData);
  }

  public async testDataExtraction(endpoint_information: any): Promise<any> {
    const formData = this.createFormData(endpoint_information);
    formData.append('data_base_path', endpoint_information.json_path_edited);
    return super.post(api.test_data_extraction, formData);
  }

  public async createMasterData(
    endpoint_information: any,
    record_type: string,
    mode: string,
  ): Promise<any> {
    const formData = this.createFormData(
      endpoint_information,
      record_type,
      mode,
    );
    formData.append('data_base_path', endpoint_information.json_path_edited);
    formData.append('data_type', endpoint_information.data_type);
    if (endpoint_information.dynamic_api) {
      formData.set('url', endpoint_information.url_user_input);
    }
    if (mode === 'create') {
      return super.post(api.create_endpoint, formData);
    } else if (mode == 'edit') {
      return super.post(api.update_endpoint, formData);
    }
  }

  public async getTypes(include_fixed_datasets = true): Promise<string[]> {
    const data = {} as dynamicObj;
    const sub_org_id = await this.getSubOrgId();
    if (sub_org_id) {
      data['sub_org_id'] = sub_org_id;
    }
    data['include_fixed_datasets'] = include_fixed_datasets;
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_master_dataset_types + '?' + querystring, {});
  }

  public async getGroupedDataSetTypes(): Promise<any> {
    return super.get(api.get_grouped_data_set_types, {});
  }

  public async getTypeFields(type_name: string): Promise<any> {
    const data = {} as dynamicObj;
    const sub_org_id = await this.getSubOrgId();
    if (sub_org_id) {
      data['sub_org_id'] = sub_org_id;
    }
    data['dataset_name'] = type_name;
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_master_dataset_fields + '?' + querystring, {});
  }

  public async getData(
    type_name: string,
    filter_data_json: any = false,
  ): Promise<any> {
    // check if filter_data_json is false
    const querystring =
      filter_data_json === false
        ? this.encodeQueryData({ data_type: type_name })
        : this.encodeQueryData({
            data_type: type_name,
            filter_data_json: filter_data_json,
          });

    return super.get(api.get_master_data_lookup + '?' + querystring, {});
  }
  public async getDataSetTypesAliases(type_name: any = false): Promise<any> {
    const querystring =
      type_name === false
        ? this.encodeQueryData({ all: true })
        : this.encodeQueryData({ all: false, data_type: type_name });
    return super.get(api.get_master_data_type_alias + '?' + querystring, {});
  }

  public async getAllDataFieldsAliases(data: dynamicObj): Promise<any> {
    // if (data_type === '') {
    //   return super.get(api.get_all_data_fields_alias);
    // }
    // {data_type: data_type}
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_all_data_fields_alias + '?' + querystring, {});
  }

  public async deleteRecord(type_name: string, id: string): Promise<any> {
    const formData = new FormData();
    formData.append('data_type', String(type_name));
    formData.append('id', id);
    return super.post(api.post_master_data_delete_record, formData);
  }
  public async updateRecord(
    type_name: string,
    data: Record<string, string>,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('data_type', String(type_name));
    formData.append('data', JSON.stringify(data));

    return super.post(api.post_master_data_update_record, formData);
  }
  public async createRecord(
    type_name: string,
    data: Record<string, string>,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('data_type', String(type_name));
    formData.append('data', JSON.stringify(data));

    return super.post(api.post_master_data_create_record, formData);
  }
  public async setDataSetTypesAlias(
    type_name: string,
    alias: string,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('data_type', String(type_name));
    if (alias !== '') {
      formData.append('alias', alias);
    }

    return super.post(api.post_master_data_set_type_alias, formData);
  }

  public async getData_V2(
    data: dynamicObj,
    filter_data_json: string,
    form_data = {},
  ): Promise<any> {
    if (!data.hasOwnProperty('sub_org_id')) {
      const sub_org_id = await this.getSubOrgId();
      if (sub_org_id) {
        data['sub_org_id'] = sub_org_id;
      }
    }
    const querystring = this.encodeQueryData(data);

    const formData = new FormData();
    for (const [key, value] of Object.entries(form_data)) {
      formData.append(key, String(value));
    }

    formData.append('filter_data_json', filter_data_json);
    return super.post(
      api.get_master_data_lookup_data_v2 + '?' + querystring,
      formData,
    );
  }

  public async getSupplierNames(): Promise<
    Array<{ supplier_number: string; supplier_name: string }>
  > {
    return super.get(api.get_supplier_names);
  }

  public async getAccountingDropdowns(apisParamsList: any): Promise<any> {
    const sub_org_id = await this.getSubOrgId();

    const updatedApisParamsList = apisParamsList.map((item: any) => {
      // Add sub_org_id to params
      const updatedParams = {
        ...item.params,
        sub_org_id: sub_org_id,
      };

      // Convert params to query string
      const querystring = this.encodeQueryData(updatedParams);

      const url = api.get_master_data_lookup_data_v2 + '?' + querystring;

      return {
        name: item.name,
        url,
        data: new FormData(),
      };
    });

    return super.executeMultiplePostRequests(updatedApisParamsList);
  }

  public async getAsnDashboard(
    data: dynamicObj,
    filter_data_json: string,
  ): Promise<any> {
    // if (!data.hasOwnProperty('sub_org_id')) {
    //   const sub_org_id = await this.getSubOrgId();
    //   data['sub_org_id'] = sub_org_id;
    // }

    const querystring = this.encodeQueryData(data);

    const formData = new FormData();
    formData.append('filter_data_json', filter_data_json);

    return super.post(
      api.get_master_data_lookup_asn_dashboard + '?' + querystring,
      formData,
    );
  }

  public async downloadAsCSV(data_type: string): Promise<any> {
    const data = {} as dynamicObj;
    const sub_org_id = await this.getSubOrgId();
    if (sub_org_id) {
      data['sub_org_id'] = sub_org_id;
    }
    data['data_type'] = data_type;
    data['delimiter'] = ',';
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_master_data_download_csv + '?' + querystring, {});
  }

  public async getLookupLov(
    lovConfigId: string,
    filter_data_json: any,
  ): Promise<any> {
    const formData = new FormData();
    if (filter_data_json) {
      formData.append('filter_data_json', filter_data_json);
    }

    return super.post(api.get_lookup_lov + lovConfigId, formData);
  }

  public async importMasterData(formData: FormData): Promise<any> {
    return super.post(api.import_master_data, formData);
  }

  public async importMasterData2(formData: FormData) {
    await this.uploadCSV(formData);
    //return super.post(api.import_master_data, formData);
  }

  public async uploadCSV(formData: FormData) {
    const chunkSize = 500;
    this.uploadState.uploadProgress = '';
    this.uploadState.uploadedRecords = 0;
    this.uploadState.totalRecords = 0;

    const file = formData.get('file') as File;
    formData.delete('file');

    const csvData = await this.readFileAsText(file);
    const lines = this.getlines(csvData);
    this.uploadState.uploadProgress = `Preparing ${lines.length} record(s)`;
    this.uploadState.totalRecords = lines.length;

    const chunks = this.chunkCSVData(lines, chunkSize);

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const data = this.copyFormData(formData);
      const rowsInChunk = chunk.length;

      if (i > 0) {
        chunk.unshift(lines[0]);
        data.delete('replace_all');
        data.append('replace_all', 'false');
      }

      const chunkCSV = this.convertToCSV(chunk);
      data.append('csv_data', chunkCSV);

      const success = await this.uploadChunk(data, rowsInChunk);
      if (!success) break;
    }
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  public copyFormData(formData: FormData): FormData {
    const newFormData = new FormData();

    formData.forEach((value, key) => {
      if (key != 'csv_data') {
        newFormData.append(key, value);
      }
    });

    return newFormData;
  }
  public getlines(csvData: string): string[] {
    if (typeof csvData !== 'string') {
      throw new Error('Input CSV data must be a string.');
    }
    // Split the input CSV string into rows based on new lines
    return csvData.split('\n');
  }

  public convertToCSV(data: string[]): string {
    if (!Array.isArray(data)) {
      throw new Error('Input data must be an array.');
    }

    // Simply join the rows with a newline
    return data.join('\n');
  }
  public chunkCSVData(rows: string[], chunkSize: any): string[][] {
    const chunkedData = [];
    for (let i = 0; i < rows.length; i += chunkSize) {
      chunkedData.push(rows.slice(i, i + chunkSize));
    }
    return chunkedData;
  }

  public async uploadChunk(formData: any, rowsCount: any) {
    try {
      const res: any = await super.post(api.import_master_data, formData);
      if (res.success == true) {
        if (res.success == true) {
          this.uploadState.uploadedRecords += rowsCount;
          this.uploadState.uploadProgress = `Records uploaded ${this.uploadState.uploadedRecords}/${this.uploadState.totalRecords}`;
        } else {
          const apimessage = res.message ? res.message : 'Unknown error';
          this.uploadState.uploadProgress = `Failed: ${String(apimessage)}`;
        }
      }

      if (this.checkUploadComplete()) {
        console.log('chunk Upload complete!');
      }
      return true;
    } catch (error) {
      console.log('Error Occured');
      // Optionally add retry logic here
      //this.uploadChunk( formData, rowsCount);
      throw error;
    }
  }

  checkUploadComplete() {
    return this.uploadState.totalRecords == this.uploadState.uploadedRecords;
  }

  public async updateBulkRecord(
    type_name: string,
    data: dynamicObj,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('data_type', String(type_name));
    formData.append('data', JSON.stringify(data));

    return super.post(api.post_master_data_update_bulk_record, formData);
  }
}
